'use client';

import React, { type ReactNode, createContext, useContext, useEffect, useRef } from 'react';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { AFFILKA_ID_PARAM_NAME, REFERRAL_CODE_PARAM_NAME } from '@/constants';
import { usePathname } from '@/i18n/routing';
import { AppStore, createAppStore } from './app-store';

type AppStoreContextType = ReturnType<typeof createAppStore>;

const AppStoreContext = createContext<AppStoreContextType | null>(null);

interface AppStoreProviderProps {
  children: ReactNode;
  accessToken?: string;
}

// Custom hook to update access token
const useUpdateAccessToken = (storeRef: React.RefObject<AppStoreContextType>, accessToken?: string) => {
  useEffect(() => {
    if (storeRef.current && accessToken !== storeRef.current.getState().accessToken) {
      storeRef.current.setState({ accessToken });
    }
  }, [accessToken]);
};

// Custom hook to track pathname history
const useTrackPathnameHistory = (storeRef: React.RefObject<AppStoreContextType>, pathname: string) => {
  useEffect(() => {
    if (storeRef.current) {
      const { currentPathname } = storeRef.current.getState();
      if (pathname !== currentPathname) {
        storeRef.current.getState().setPathnames(pathname);
      }
    }
  }, [pathname]);
};

/**
 * Custom hook to extract "referralCode" and "stag" from URL params
 *
 * - Referral code is used to set the referral code in the store
 * - Stag is used to set the affilkaId in the store (used for affiliate program)
 *
 * @param storeRef - The store reference
 */
const useExtractUrlParameters = (storeRef: React.RefObject<AppStoreContextType>) => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const referralCode = params.get(REFERRAL_CODE_PARAM_NAME);
    const affilkaId = params.get(AFFILKA_ID_PARAM_NAME);

    if (storeRef.current) {
      if (referralCode) {
        storeRef.current.setState({ referralCode });
      }
      if (affilkaId) {
        storeRef.current.setState({ affilkaId });
      }
    }
  }, []); // Run this effect once on mount
};

/**
 * A provider that wraps the app with the AppStore with context.
 * - The store is created once and shared between all components.
 * - The store is created with the accessToken provided.
 * - The store is only used client side.
 */
export const AppStoreProvider: React.FC<AppStoreProviderProps> = ({ children, accessToken }) => {
  const storeRef = useRef<AppStoreContextType>(createAppStore({ accessToken }));
  const pathname = usePathname();

  // Use custom hooks
  useUpdateAccessToken(storeRef, accessToken);
  useTrackPathnameHistory(storeRef, pathname);
  useExtractUrlParameters(storeRef);

  return <AppStoreContext.Provider value={storeRef.current}>{children}</AppStoreContext.Provider>;
};

export const useAppStore = <T,>(selector: (state: AppStore) => T) => {
  const store = useContext(AppStoreContext);

  if (!store) {
    throw new Error('useAppStore must be used within an AppStoreProvider client side.');
  }

  return useStoreWithEqualityFn(store, selector);
};

export const useAppStorePersist = () => {
  const store = useContext(AppStoreContext);

  if (!store) {
    throw new Error('useAppStorePersist must be used within an AppStoreProvider client side.');
  }

  return store;
};
